package com.shadowings.gero.presentation.screen

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.animation.scaleOut
import androidx.compose.foundation.Image
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.pager.VerticalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.data.lessons.chapter1.getIntroductionHieroglyphsContent
import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.LessonIds
import com.shadowings.gero.domain.model.UniliteralExplanation
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.ImagePopup
import com.shadowings.gero.presentation.component.GeroButton
import com.shadowings.gero.presentation.component.TopBar
import com.shadowings.gero.presentation.component.avatars.AlynAvatar
import com.shadowings.gero.presentation.component.avatars.MaskoAvatar
import com.shadowings.gero.presentation.component.avatars.NiloAvatar
import com.shadowings.gero.presentation.component.avatars.ZetaAvatar
import com.shadowings.gero.presentation.component.explanation.HieroExplanationComposable
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import com.shadowings.gero.presentation.viewmodel.ExplanationViewModel
import com.shadowings.gero.util.systemLanguageCode
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.continua_btn
import gero.composeapp.generated.resources.empty_explanation_desc
import gero.composeapp.generated.resources.triangle
import gero.composeapp.generated.resources.triangle_desc
import io.github.vinceglb.confettikit.compose.ConfettiKit
import io.github.vinceglb.confettikit.core.Party
import io.github.vinceglb.confettikit.core.Position
import io.github.vinceglb.confettikit.core.emitter.Emitter
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.DurationUnit
import kotlin.time.toDuration

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
fun ExplanationScreenPreview() {
    AppTheme {
        Surface {
            ExplanationScreenContent(
                navController = rememberNavController(),
                lessonId = "uni_1",
                content = getIntroductionHieroglyphsContent(),
                userData = UserData(),
                hiero = listOf(),
                onExplanationCompleted = {},
            )
        }
    }
}

@Composable
fun ExplanationScreen(
    navController: NavController,
    viewModel: ExplanationViewModel = koinViewModel(),
) {
    val userData by viewModel.userData.collectAsState()
    val hieros = viewModel.hieros
    val content by viewModel.content.collectAsState()

    ExplanationScreenContent(
        navController = navController,
        lessonId = viewModel.lessonId,
        content = content,
        hiero = hieros,
        userData = userData,
        onExplanationCompleted = { viewModel.onExplanationCompleted() },
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ExplanationScreenContent(
    navController: NavController,
    lessonId: String,
    content: List<IExplanation>,
    hiero: List<Hiero>,
    userData: UserData,
    onExplanationCompleted: () -> Unit,
) {
    val deviceInfo = rememberDeviceInfo()
    val isWebFull = deviceInfo.deviceClass == DeviceClass.WEB_FULL
    if (content.isEmpty()) {
        Scaffold(
            modifier = Modifier.fillMaxSize(),
            topBar = {
                if (isWebFull) {
                    TopBar(
                        strikeCount = userData.strike,
                        level = com.shadowings.gero.domain.model.GameConstants.getPlayerLevel(
                            userData.experience
                        ),
                        starCount = userData.stars.values.sum(),
                        heartsCount = if (userData.isFullVersionUnlocked) 999 else userData.hearts,
                        showHearts = !userData.isFullVersionUnlocked,
                        hasAvailableStreakRestoration = userData.streakRestorationTokens.values.any { !it } &&
                            !userData.streakRestorationBadgeSeen,
                        onBackClick = { navController.popBackStack() }
                    )
                }
            }
        ) { padding ->
            Box(
                Modifier.fillMaxSize()
                    .padding(padding)
                    .then(if (isWebFull) Modifier.padding(64.dp) else Modifier)
            ) {
                Text(
                    modifier = Modifier.fillMaxWidth(),
                    text = stringResource(Res.string.empty_explanation_desc),
                    style = MaterialTheme.typography.titleMedium,
                    textAlign = TextAlign.Center
                )
            }
        }
        return
    }

    val pageCount = content.size
    val pagerState = rememberPagerState(0) { pageCount }
    val endReached = remember { mutableStateOf(false) }
    val hapticFeedback = LocalHapticFeedback.current
    val showConfetti = remember { mutableStateOf(false) }

    val lazyListState = rememberLazyListState()

    Scaffold(
        modifier = Modifier.fillMaxSize(),
        topBar = {
            if (isWebFull) {
                TopBar(
                    strikeCount = userData.strike,
                    level = com.shadowings.gero.domain.model.GameConstants.getPlayerLevel(
                        userData.experience
                    ),
                    starCount = userData.stars.values.sum(),
                    heartsCount = if (userData.isFullVersionUnlocked) 999 else userData.hearts,
                    showHearts = !userData.isFullVersionUnlocked,
                    hasAvailableStreakRestoration = userData.streakRestorationTokens.values.any { !it } &&
                            !userData.streakRestorationBadgeSeen,
                    onBackClick = { navController.popBackStack() }
                )
            }
        }
    ) { padding ->
        val isLastItemVisible = remember(pageCount) {
            derivedStateOf {
                val lastVisibleItem = lazyListState.layoutInfo.visibleItemsInfo.lastOrNull()
                lastVisibleItem != null && lastVisibleItem.index == pageCount - 1
            }
        }

        LaunchedEffect(
            pagerState.currentPage,
            lazyListState.firstVisibleItemIndex,
            isLastItemVisible.value
        ) {
            if (pagerState.currentPage == pageCount - 1 && !endReached.value) {
                endReached.value = true
            }

            val currentPage = pagerState.currentPage
            val currentItem = content.getOrNull(currentPage)
            if (currentItem is ExplanationContent && lessonId == LessonIds.TRANSLITERATION_READING) {
                when (currentPage) {
                    // exp_content_transliteration_reading_point_8
                    7 -> {
                        hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                        showConfetti.value = true
                    }
                }
            }
        }

        val rawCurrentPage = pagerState.currentPage
        val currentPage = rawCurrentPage.coerceIn(0, pageCount - 1)
        val item = content[currentPage]

        Box(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
                    .then(if (isWebFull) Modifier.padding(64.dp) else Modifier)
                    .semantics { testTag = "explanation_screen" },
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (!deviceInfo.isLandscape || isWebFull) {
                    ElevatedCard(
                        modifier = Modifier
                            .padding(16.dp)
                            .border(
                                width = 2.dp,
                                color = MaterialTheme.colorScheme.onSecondaryContainer,
                                shape = CardDefaults.shape
                            )
                            .semantics { testTag = "explanation_title_card" },
                        colors = CardDefaults.cardColors(
                            containerColor = MaterialTheme.colorScheme.secondaryContainer
                        )
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Column(
                                Modifier.fillMaxWidth().padding(16.dp),
                                horizontalAlignment = Alignment.Start
                            ) {
                                Text(
                                    modifier = Modifier.fillMaxWidth(),
                                    text = stringResource(
                                        item.title,
                                        *item.titleArgs.toTypedArray()
                                    ),
                                    style = MaterialTheme.typography.titleMedium.copy(
                                        fontSize = 22.sp,
                                        fontWeight = FontWeight.Bold
                                    ),
                                    color = MaterialTheme.colorScheme.onSecondaryContainer,
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    }
                }
                VerticalPager(
                    state = pagerState,
                    modifier = Modifier.weight(1f)
                        .semantics { testTag = "explanation_pager" }
                ) { page ->
                    when (val pageItem = content[page]) {
                        is ExplanationContent -> BaseExplanationComposable(
                            item = pageItem,
                            page = page,
                            userData = userData,
                            fillMaxHeight = true
                        )

                        is UniliteralExplanation -> HieroExplanationComposable(
                            hiero = hiero,
                            item = pageItem,
                            page = page,
                            userData = userData,
                            languageCode = userData.language.ifBlank { systemLanguageCode() },
                            fillMaxHeight = true
                        )
                    }
                }

                AnimatedVisibility(
                    visible = endReached.value,
                    enter = fadeIn() + scaleIn(),
                    exit = fadeOut() + scaleOut()
                ) {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        GeroButton(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)
                                .height(64.dp),
                            title = stringResource(Res.string.continua_btn),
                            testTagId = "explanation_btn_continue"
                        ) {
                            onExplanationCompleted()
                            navController.popBackStack()
                        }
                        Spacer(Modifier.size(if (deviceInfo.isLandscape) 8.dp else 16.dp))
                    }
                }
            }

            // Coriandoli quando si spara la vibrazione
            if (showConfetti.value) {
                ConfettiKit(
                    modifier = Modifier.fillMaxSize(),
                    parties = listOf(
                        Party(
                            speed = 0f, maxSpeed = 30f, damping = 0.9f, spread = 360,
                            position = Position.Relative(0.5, 0.3),
                            emitter = Emitter(duration = 2500L.toDuration(DurationUnit.MILLISECONDS)).max(
                                150
                            )
                        )
                    )
                )
            }
        }
    }
}

@Composable
fun BaseExplanationComposable(
    item: ExplanationContent,
    userData: UserData,
    page: Int,
    fillMaxHeight: Boolean = true
) {
    val showFullscreenImage = remember { mutableStateOf(false) }


    BoxWithConstraints(
        modifier = if (fillMaxHeight) Modifier.fillMaxSize() else Modifier.fillMaxWidth()
    ) {
        val deviceInfo = rememberDeviceInfo()
        val isLandscape = deviceInfo.isLandscape

        val isPhoneLargePortrait = deviceInfo.deviceClass == DeviceClass.PHONE_LARGE && !isLandscape
        val avatarSize = when (deviceInfo.deviceClass) {
            DeviceClass.PHONE_SMALL -> 64.dp
            DeviceClass.PHONE_MEDIUM -> 80.dp
            DeviceClass.PHONE_LARGE -> if (isPhoneLargePortrait) 120.dp else 110.dp
            DeviceClass.TABLET_MEDIUM -> 120.dp
            DeviceClass.TABLET_LARGE -> 150.dp
            DeviceClass.WEB_FULL -> 150.dp
        }

        ElevatedCard(
            modifier = Modifier
                .then(if (fillMaxHeight) Modifier.fillMaxSize() else Modifier.fillMaxWidth())
                .padding(
                    horizontal = if (isLandscape) 8.dp else (if (fillMaxHeight) 32.dp else 16.dp),
                    vertical = if (isLandscape) 4.dp else (if (fillMaxHeight) 64.dp else 8.dp)
                )
                .semantics { testTag = "explanation_page_$page" },
        ) {
            Column(
                modifier = Modifier
                    .then(if (fillMaxHeight) Modifier.fillMaxSize() else Modifier.fillMaxWidth())
                    .padding(
                        horizontal = if (isLandscape) 12.dp else 16.dp,
                        vertical = if (isLandscape) 8.dp else 16.dp
                    ),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (isLandscape && (item.composable != null || item.image != null)) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        // Left column: Avatar + Speech bubble (content)
                        Column(
                            modifier = Modifier.weight(1.2f),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            when (item.character) {
                                Characters.Nilo -> {
                                    NiloAvatar(
                                        face = item.face,
                                        userData = userData
                                    )
                                }

                                Characters.Zeta -> {
                                    ZetaAvatar(
                                        face = item.face,
                                        userData = userData
                                    )
                                }

                                Characters.Masko -> {
                                    MaskoAvatar(
                                        face = item.face,
                                        userData = userData
                                    )
                                }

                                else -> AlynAvatar(
                                    face = item.face,
                                    userData = userData
                                )
                            }

                            item.content?.let { contentRes ->
                                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                    Image(
                                        modifier = Modifier.width(10.dp),
                                        painter = painterResource(Res.drawable.triangle),
                                        contentDescription = stringResource(Res.string.triangle_desc),
                                        colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
                                    )
                                    OutlinedCard {
                                        Text(
                                            modifier = Modifier.padding(6.dp),
                                            text = stringResource(
                                                contentRes,
                                                *item.contentArgs.toTypedArray()
                                            ),
                                            style = MaterialTheme.typography.bodyMedium,
                                            textAlign = TextAlign.Center
                                        )
                                    }
                                }
                            }
                        }

                        // Right column: Composable + optional example / image
                        Column(
                            modifier = Modifier.weight(1.8f),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            item.example?.let {
                                Text(
                                    text = it,
                                    style = MaterialTheme.typography.displayMedium,
                                    fontWeight = FontWeight.Bold,
                                    textAlign = TextAlign.Center
                                )
                            }
                            item.image?.let {
                                val contentDescription = item.content?.let { content ->
                                    stringResource(content, *item.contentArgs.toTypedArray())
                                }
                                Image(
                                    modifier = Modifier
                                        .fillMaxWidth(item.imageSize * 0.7f)
                                        .then(
                                            if (item.fullscreenable) {
                                                Modifier.clickable {
                                                    showFullscreenImage.value = true
                                                }
                                            } else {
                                                Modifier
                                            }
                                        ),
                                    painter = painterResource(it),
                                    contentDescription = contentDescription,
                                    colorFilter = if (item.applyFilter) {
                                        ColorFilter.tint(MaterialTheme.colorScheme.onSurface)
                                    } else {
                                        null
                                    }
                                )

                                if (item.fullscreenable && showFullscreenImage.value) {
                                    ImagePopup(showFullscreenImage, it, item)
                                }
                            }
                            item.composable?.let { it() }
                        }
                    }
                } else {
                    when (item.character) {
                        Characters.Nilo -> {
                            NiloAvatar(
                                face = item.face,
                                userData = userData
                            )
                        }

                        Characters.Zeta -> {
                            ZetaAvatar(
                                face = item.face,
                                userData = userData
                            )
                        }

                        Characters.Masko -> {
                            MaskoAvatar(
                                face = item.face,
                                userData = userData
                            )
                        }

                        else -> AlynAvatar(
                            face = item.face,
                            userData = userData
                        )
                    }
                    Spacer(modifier = Modifier.height(if (isLandscape) 8.dp else 16.dp))
                    item.content?.let {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Image(
                                modifier = Modifier.width(12.dp),
                                painter = painterResource(Res.drawable.triangle),
                                contentDescription = stringResource(Res.string.triangle_desc),
                                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
                            )
                            OutlinedCard {
                                Text(
                                    modifier = Modifier.padding(8.dp),
                                    text = stringResource(it, *item.contentArgs.toTypedArray()),
                                    style = if (deviceInfo.deviceClass == DeviceClass.PHONE_SMALL) {
                                        MaterialTheme.typography.bodyMedium
                                    } else {
                                        MaterialTheme.typography.bodyLarge
                                    },
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                        Spacer(modifier = Modifier.height(if (isLandscape) 8.dp else 16.dp))
                    }
                    item.example?.let {
                        Text(
                            text = it,
                            style = if (isLandscape) MaterialTheme.typography.displayMedium else MaterialTheme.typography.displayLarge,
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(if (isLandscape) 8.dp else 16.dp))
                    }
                    item.image?.let {
                        val contentDescription = item.content?.let { content ->
                            stringResource(content, *item.contentArgs.toTypedArray())
                        }
                        Image(
                            modifier = Modifier
                                .fillMaxWidth(if (isLandscape) item.imageSize * 0.7f else item.imageSize)
                                .then(
                                    if (item.fullscreenable) {
                                        Modifier.clickable { showFullscreenImage.value = true }
                                    } else {
                                        Modifier
                                    }
                                ),
                            painter = painterResource(it),
                            contentDescription = contentDescription,
                            colorFilter = if (item.applyFilter) {
                                ColorFilter.tint(MaterialTheme.colorScheme.onSurface)
                            } else {
                                null
                            }
                        )

                        if (item.fullscreenable && showFullscreenImage.value) {
                            ImagePopup(showFullscreenImage, it, item)
                        }
                    }
                    item.composable?.let { it() }
                }
            }
        }
    }
}
